import {FC, useState, useEffect} from 'react';
import {useQueries, useQuery} from '@tanstack/react-query';
import uniqBy from 'lodash/uniqBy';
import {Select} from '@shipwell/shipwell-ui';
import {Address, Contract, ApplicableContract} from '@shipwell/backend-core-singlerequestparam-sdk';
import {formatAddressContains, getContractParamsFromContractOrLane} from './utils';
import {getContracts, getContractsApplicableForShipment} from 'App/api/contracts/typed';
import {CONTRACTS_QUERY_KEY, APPLICABLE_CONTRACTS_KEY} from 'App/data-hooks/queryKeys';
import {RATE_TYPE_PER_MILE, RATE_TYPE_RATE_TABLE} from 'App/containers/userCompany/rateTables/constants';
import {createGrammaticList} from 'App/utils/grammar';
import {isContract} from 'App/data-hooks/contracts/useSelectedContract';

type ContractSelectBase = {
  contract?: Contract;
  modes?: string[];
  equipment?: string[];
  status?: string;
  origin?: Address;
  destination?: Address;
  additionalStops?: Address[] | 'isMulti';
  currencyOfRecord: string;
  label?: string;
  disabled?: boolean;
  shipmentId?: string;
};

type SingleContractSelect = ContractSelectBase & {
  isMulti?: false;
  onChange: (newContract?: Contract | ApplicableContract) => void;
};
type MultiContractSelect = ContractSelectBase & {
  isMulti: true;
  onChange: (newContract?: (Contract | ApplicableContract)[]) => void;
};

export const ContractSelect: FC<SingleContractSelect | MultiContractSelect> = ({
  contract,
  onChange,
  modes,
  equipment,
  status,
  origin,
  additionalStops,
  destination,
  currencyOfRecord,
  label = 'Tender from Contract',
  disabled = false,
  isMulti,
  shipmentId
}) => {
  const [input, setInput] = useState('');

  useEffect(() => {
    return () => (isMulti ? onChange([]) : onChange(undefined));
    // need to clear the selected contract/contracts on umount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryParams = {
    q: input,
    ...({modes} || null),
    ...(equipment && {equipmentTypes: equipment}),
    ...({status} || null)
  };

  // uses backend matching when a shipmentId is available
  const {data: {data: contractsApplicableForShipment} = {}, ...applicableContractsQuery} = useQuery(
    [APPLICABLE_CONTRACTS_KEY, shipmentId],
    {
      queryFn: () => {
        if (!shipmentId) return;
        return getContractsApplicableForShipment({shipmentId});
      },
      enabled: !!shipmentId
    }
  );

  // otherwise frontend can do its own matching
  const [contractsQuery, contractsQueryByState] = useQueries({
    queries: [
      {
        queryKey: [CONTRACTS_QUERY_KEY, queryParams],
        queryFn: () =>
          getContracts({
            // limit contract call to only show contracts that match the shipment's mode and equipment, origin and destination
            ...queryParams,
            originsContains: formatAddressContains(origin),
            destinationsContains: formatAddressContains(destination)
          }),
        enabled: !shipmentId
      },
      {
        queryKey: [CONTRACTS_QUERY_KEY, queryParams, origin?.state_province, destination?.state_province],
        queryFn: () =>
          getContracts({
            // a second query is needed to get all contracts that match on state
            ...queryParams,
            originsStateProvince: origin?.state_province ? [origin.state_province] : [],
            destinationsStateProvince: destination?.state_province ? [destination.state_province] : []
          }),
        enabled: !shipmentId
      }
    ]
  });
  const filteredContractsByState = contractsQueryByState.data?.results?.filter((contract) =>
    [contract.origins, contract.destinations].every((location, index) =>
      location?.some(
        (location) =>
          ((location.address_1 === [origin, destination][index]?.address_1 &&
            location.postal_code === [origin, destination][index]?.postal_code) ||
            (!location.address_1 &&
              (!(location.city && location.postal_code) ||
                (!location.postal_code && location.city === [origin, destination][index]?.city) ||
                location.postal_code === [origin, destination][index]?.postal_code))) &&
          location.state_province === [origin, destination][index]?.state_province
      )
    )
  );

  const combinedContractsResults = [
    //backend matching if available
    ...(contractsApplicableForShipment ? contractsApplicableForShipment : []),
    ...uniqBy(
      [
        //frontend-matched contracts
        ...(contractsQuery.data?.results ? contractsQuery.data?.results : []),
        //filtered state-only contracts
        ...(filteredContractsByState ? filteredContractsByState : [])
      ],
      (contract) => contract.id
    ).filter((contract) => {
      // if user is attempting to BULK tender to carrier, we should exclude all contracts with additional stops specified
      if (additionalStops === 'isMulti') {
        return !contract?.additional_stops?.length;
      }
      // if no additional stops specified, we should show to user
      if (!contract?.additional_stops?.length || !additionalStops) {
        return true;
      }
      // if additional stops are specified on the contract, we should only show the contract if the additionalStops match exactly
      // first check they are at least the same length
      if (contract.additional_stops.length !== additionalStops?.length) {
        return false;
      }
      // sort stops by order_index
      const sortedContractAdditionalStops = [...contract.additional_stops].sort((a, b) => {
        // there is currently an SDK error that doesn not include order_index in the additional_stops of the contract
        if (
          !('order_index' in a) ||
          !('order_index' in b) ||
          typeof a.order_index !== 'number' ||
          typeof b.order_index !== 'number'
        ) {
          return 1;
        }
        if (a.order_index < b.order_index) {
          return -1;
        }
        if (a.order_index > b.order_index) {
          return 1;
        }
        return 0;
      });
      for (let i = 0; i < additionalStops.length; i++) {
        // check if each additional stop's formatted address exists in the same index after sorting by order_index
        if (additionalStops[i].formatted_address !== sortedContractAdditionalStops[i].formatted_address) {
          return false;
        }
      }
      return true;
    })
  ];

  const contractOption = (option: Contract | ApplicableContract, {context}: {context: string}) => {
    const isContractOption = isContract(option);
    const contract = isContractOption ? option : option.contract;
    const {
      origins: contractOrigins,
      destinations: contractDestinations,
      rateType,
      rate,
      modes,
      equipmentTypes
    } = getContractParamsFromContractOrLane({
      contract,
      lane: isContractOption ? undefined : option.contract_lane
    });

    // if the Select is closed, only show the name
    if (context === 'value') {
      return contract?.name || '--';
    }
    // else show more info about contract
    const joiner = ' · ';
    const origins = contractOrigins?.reduce(
      (acc, origin) => (origin?.formatted_address ? [...acc, origin.formatted_address] : acc),
      [] as string[]
    );
    const destinations = contractDestinations?.reduce(
      (acc, destination) => (destination?.formatted_address ? [...acc, destination.formatted_address] : acc),
      [] as string[]
    );

    const displayRate = `${currencyOfRecord || contract?.rate_currency || 'USD'} ${
      rateType === RATE_TYPE_RATE_TABLE ? 'Rate Table' : rate?.toLocaleString() || ''
    } ${rateType === RATE_TYPE_PER_MILE ? '/mile' : ''}`;

    return (
      <div className="flex flex-col">
        <span className="font-bold">{contract?.name || '--'}</span>
        <span>
          {displayRate}
          {joiner}
          {contract?.carrier_name || '--'}
          {joiner}
          {origins && destinations && origins.length > 0 && destinations.length > 0 ? (
            <span>
              {createGrammaticList(origins)} &gt; {createGrammaticList(destinations)}
              {joiner}
            </span>
          ) : null}
          {modes ? createGrammaticList(modes.map((mode) => mode.code || '--')) : null}
          {joiner}
          {equipmentTypes ? createGrammaticList(equipmentTypes.map((equipment) => equipment.name || '--')) : null}
          {joiner}
          {contract?.service_level_agreement_name}
        </span>
      </div>
    );
  };

  const handleOptionLabel = (option: Contract | ApplicableContract) => {
    const contract = isContract(option) ? option : option.contract;
    // we want to be able to see contracts who's name OR carrier_name match the search string
    if (contract?.name.toLowerCase().includes(input.toLowerCase())) {
      return contract.name;
    }
    return contract?.carrier_name || '--';
  };

  return (
    <Select
      disabled={disabled}
      label={label}
      clearable
      onInputChange={(search: string) => setInput(search)}
      value={contract}
      onChange={onChange}
      formatOptionLabel={contractOption}
      getOptionLabel={handleOptionLabel}
      getOptionValue={(option: Contract | ApplicableContract) => (isContract(option) ? option.id : option.contract?.id)}
      isLoading={contractsQuery.isFetching || contractsQueryByState.isFetching || applicableContractsQuery.isFetching}
      options={combinedContractsResults}
      isMulti={isMulti}
    />
  );
};
